/// <amd-module name="Build/DeletedPipelines/DeletedPipelinesView" />
import * as React from "react";

import { DeletedPipelineData, PipelineDeletionRetentionSettings } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { clearTreeStateOnUnmount } from "Build/Common/Library/StateHandler";
import { IDeletedPipelinesDataService } from "Build/DeletedPipelines/DeletedPipelinesDataService";
import * as Resources from "Build/DeletedPipelines/Resources";

import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITableColumn, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { AgoFormat } from "VSSUI/Utilities/Date";

import { IObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export interface IDeletedPipelinesViewContentProps extends IVssComponentProperties {}

export interface IDeletedPipelinesViewContentState {}

class DeletedPipelinesViewContent extends VssComponent<IDeletedPipelinesViewContentProps, IDeletedPipelinesViewContentState> {
    private _dataService: IDeletedPipelinesDataService;
    private _itemProvider: IObservableArray<DeletedPipelineData>;
    private _ttiMarked: boolean;

    constructor(props: IDeletedPipelinesViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._dataService = context.pageContext.getService<IDeletedPipelinesDataService>("IDeletedPipelinesDataService");
        this._itemProvider = this._dataService.data;
        this._ttiMarked = false;
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="deleted-pipelines-page">
                    <Header
                        title={Resources.DeletedPipelinesTitle}
                        titleSize={TitleSize.Large}
                        backButtonProps={{ onClick: this._onClickHeaderBack }}
                    />
                    <div className="deleted-pipelines-page-content">
                        <Observer
                            pipelineDeletionRetentionSettings={this._dataService.pipelineDeletionRetentionSettings}
                            hasPipelines={this._dataService.hasDeletedPipelines}
                        >
                            {(observerProps: { pipelineDeletionRetentionSettings: PipelineDeletionRetentionSettings; hasPipelines: boolean }) => {
                                return (
                                    <>
                                        <MessageCard severity={MessageCardSeverity.Info} className="deleted-pipelines-card">
                                            <span>
                                                {format(
                                                    Resources.DeletedPipelinesMessage,
                                                    observerProps.pipelineDeletionRetentionSettings.deleteDefinitionJobScheduleDays
                                                )}
                                            </span>
                                        </MessageCard>
                                        {observerProps.hasPipelines ? (
                                            <Card className="deleted-pipelines-card bolt-table-card">
                                                <Table columns={this._tableColumns} itemProvider={this._itemProvider} singleClickActivation={false} />
                                            </Card>
                                        ) : null}
                                    </>
                                );
                            }}
                        </Observer>
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount() {
        this._markTTI();
    }

    public componentWillUnmount() {
        clearTreeStateOnUnmount(this.context.pageContext);
    }

    private _onClickHeaderBack = () => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const landingPageLink = linkingService.getDefinitionUrl(undefined);
        onClickFPS(this.context.pageContext, landingPageLink, true);
    };

    private _renderNameCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<DeletedPipelineData>,
        tableItem: DeletedPipelineData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontWeightSemiBold fontSizeM scroll-hidden"
            >
                <Icon iconName="Rocket" className="icon-margin font-size-ml font-weight-normal" />
                <span className="inline-flex-row scroll-hidden">
                    <span className="text-ellipsis">{tableItem.name}</span>
                    {tableItem.isDraft && <sup className="font-size-xxs deleted-pipelines-draft-indicator">{Resources.Draft}</sup>}
                </span>
            </SimpleTableCell>
        );
    };

    private _renderUserCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<DeletedPipelineData>,
        tableItem: DeletedPipelineData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden"
            >
                <Persona identity={tableItem.deletedBy} size={PersonaSize.size24} className="icon-large-margin" />
                <span className="text-ellipsis">{tableItem.deletedBy.displayName}</span>
            </SimpleTableCell>
        );
    };

    private _renderDateCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<DeletedPipelineData>,
        tableItem: DeletedPipelineData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden"
            >
                <Ago date={tableItem.deletedDate} format={AgoFormat.Compact} />
            </SimpleTableCell>
        );
    };

    private _renderDestructionCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<DeletedPipelineData>,
        tableItem: DeletedPipelineData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden"
            >
                <span className="text-ellipsis">
                    {tableItem.daysRemaining === 1 ? Resources.DaysRemainingSingular : format(Resources.DaysRemaining, tableItem.daysRemaining)}
                </span>
            </SimpleTableCell>
        );
    };

    private _renderActionCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<DeletedPipelineData>,
        tableItem: DeletedPipelineData
    ): JSX.Element => {
        const restoreDefinition = () => {
            this._dataService.restore(tableItem.id);
        };

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden bolt-table-cell-side-action"
            >
                <div className="deleted-pipelines-right-align flex-row">
                    <Button
                        primary={false}
                        className="bolt-table-cell-content-reveal"
                        subtle={true}
                        iconProps={{ iconName: "RevToggleKey" }}
                        ariaLabel={Resources.Restore}
                        tooltipProps={{ text: Resources.Restore }}
                        onClick={restoreDefinition}
                        excludeTabStop={true}
                    />
                </div>
            </SimpleTableCell>
        );
    };

    private _markTTI() {
        if (!this._ttiMarked) {
            this._ttiMarked = true;
            const service = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            service.markTimeToInteractive("Build", "Pipelines.DeletedPipelines");
        }
    }

    // tslint:disable-next-line:member-ordering
    private _tableColumns: ITableColumn<DeletedPipelineData>[] = [
        {
            id: "name",
            width: -20,
            name: Resources.PipelineColumnName,
            className: "deleted-pipelines-cell",
            renderCell: this._renderNameCell,
            columnStyle: TableColumnStyle.Primary
        },
        {
            id: "user",
            width: -20,
            name: Resources.DeletedColumnName,
            className: "deleted-pipelines-cell",
            renderCell: this._renderUserCell,
            columnStyle: TableColumnStyle.Secondary
        },
        {
            id: "date",
            width: -20,
            name: Resources.DateColumnName,
            className: "deleted-pipelines-cell",
            renderCell: this._renderDateCell,
            columnStyle: TableColumnStyle.Secondary
        },
        {
            id: "destruction",
            width: -20,
            name: Resources.PermanentDeletionColumnName,
            className: "deleted-pipelines-cell",
            renderCell: this._renderDestructionCell,
            columnStyle: TableColumnStyle.Secondary
        },
        {
            id: "action",
            width: -20,
            className: "deleted-pipelines-cell",
            renderCell: this._renderActionCell,
            columnStyle: TableColumnStyle.Secondary
        }
    ];
}

Components.add("deleted-pipelines-view-content", DeletedPipelinesViewContent);
